import React from 'react';
import './InfoPage.css';
import TopBar from '../components/TopBar';
import {
  Info,
  ShieldCheck,
  Database,
  FileText,
  Sparkles,
  ExternalLink,
  Zap,
  Clock,
  Trophy,
  Code2,
  Heart,
} from 'lucide-react';

const VERSION = '2.6';
const YEAR = new Date().getFullYear();

const TECH_STACK = [
  { label: 'React', sub: 'UI framework' },
  { label: 'Firebase', sub: 'Database & Auth' },
  { label: 'Lucide', sub: 'Icon system' },
  { label: 'FantaMaster', sub: 'Dati giocatori' },
];

const CHANGELOG = [
  { version: '2.6', date: 'Ago 2026', note: 'Introduzione strategie e importazione dalle aste.' },
  { version: '2.5', date: 'Ago 2026', note: 'Gestione scambi pubblici.' },
  { version: '2.4', date: 'Lug 2026', note: 'Introduzione gestione infortunati.' },
  { version: '2.3', date: 'Lug 2026', note: 'Introduzione analisi rose.' },
  { version: '2.2', date: 'Giu 2026', note: 'Gestione aste pubbliche e private.' },
  { version: '2.2', date: 'Mar 2026', note: 'Introduzione FantaQuizz.' },
  { version: '2.1', date: 'Mar 2026', note: 'Introduzione gestione rose mantra.' },
  { version: '2.0', date: 'Giu 2025', note: 'Redesign completo UI, dark/light mode, dashboard avanzata.' },
  { version: '1.5', date: 'Feb 2025', note: 'Introduzione modulo aste con gestione rosa in tempo reale.' },
  { version: '1.0', date: 'Set 2024', note: 'Prima release: lista giocatori, statistiche e sync Firebase.' },
];

function SectionCard({ icon: Icon, title, accent, children }) {
  return (
    <article className={`ip-card ip-card--${accent}`}>
      <div className="ip-card__head">
        <div className={`ip-card__icon ip-card__icon--${accent}`}>
          <Icon size={17} />
        </div>
        <h2 className="ip-card__title">{title}</h2>
      </div>
      <div className="ip-card__body">{children}</div>
    </article>
  );
}

function TechBadge({ label, sub }) {
  return (
    <div className="ip-tech">
      <span className="ip-tech__label">{label}</span>
      <span className="ip-tech__sub">{sub}</span>
    </div>
  );
}

function ChangelogRow({ version, date, note, latest }) {
  return (
    <div className={`ip-changelog-row ${latest ? 'ip-changelog-row--latest' : ''}`}>
      <div className="ip-changelog-row__left">
        <span className="ip-changelog-row__ver">v{version}</span>
        <span className="ip-changelog-row__date">{date}</span>
      </div>
      <p className="ip-changelog-row__note">{note}</p>
    </div>
  );
}

export default function InfoPage() {
  return (
    <main className="container ip-page">
      <TopBar />

      {/* ── HERO ── */}
      <section className="ip-hero">
        <div className="ip-hero__bg" aria-hidden="true" />

        <div className="ip-hero__inner">
          <div className="ip-hero__eyebrow">
            <Info size={12} />
            <span>Informazioni</span>
          </div>

          <div className="ip-hero__title-row">
            <div className="ip-hero__ver-badge">
              <Zap size={11} />
              v{VERSION}
            </div>
            <h1 className="ip-hero__h1">FantApp!</h1>
          </div>

          <p className="ip-hero__desc">
            La cabina di regia per il tuo fantacalcio. Dati, aste, rosa e formazione
            in un'unica interfaccia — veloce, leggibile e sempre sincronizzata.
          </p>

          <div className="ip-hero__meta">
            <div className="ip-hero__meta-item">
              <Clock size={13} />
              <span>Aggiornata {YEAR}</span>
            </div>
            <div className="ip-hero__meta-item">
              <Trophy size={13} />
              <span>Stagione 2026/27</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── INFO GRID ── */}
      <div className="ip-grid">
        <SectionCard icon={Sparkles} title="Chi siamo" accent="violet">
          <p>
            FantApp è un progetto personale pensato per chi vuole dominare l'asta di fantacalcio.
            Offre una dashboard con dati aggiornati, strumenti per la gestione della rosa
            e presto funzionalità AI per trovare i giocatori nascosti.
          </p>
        </SectionCard>

        <SectionCard icon={FileText} title="Termini di utilizzo" accent="blue">
          <p>
            L'app è fornita a scopo informativo e organizzativo. I contenuti mostrati
            vanno usati come supporto durante l'asta e la gestione della rosa.
            Non è previsto alcun uso commerciale.
          </p>
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="Privacy" accent="teal">
          <p>
            FantApp tratta esclusivamente i dati necessari al funzionamento dell'autenticazione
            e alla sincronizzazione dei giocatori. Nessun dato viene ceduto a terze parti
            né utilizzato per profilazione pubblicitaria.
          </p>
        </SectionCard>

        <SectionCard icon={Database} title="Fonti dati" accent="amber">
          <p>
            Statistiche, ruoli e quotazioni dei giocatori sono forniti da{' '}
            <a
              href="https://www.fantamaster.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="ip-link"
            >
              FantaMaster
              <ExternalLink size={11} className="ip-link__icon" />
            </a>
            . I dati vengono sincronizzati su Firebase e aggiornati ad ogni nuova sessione.
          </p>
        </SectionCard>
      </div>

      {/* ── TECH STACK ── */}
      <section className="ip-section">
        <div className="ip-section__head">
          <Code2 size={15} />
          <h2>Stack tecnologico</h2>
        </div>
        <div className="ip-tech-grid">
          {TECH_STACK.map((t) => (
            <TechBadge key={t.label} {...t} />
          ))}
        </div>
      </section>

      {/* ── CHANGELOG ── */}
      <section className="ip-section">
        <div className="ip-section__head">
          <Clock size={15} />
          <h2>Changelog</h2>
        </div>
        <div className="ip-changelog">
          {CHANGELOG.map((c, i) => (
            <ChangelogRow key={c.version} {...c} latest={i === 0} />
          ))}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="ip-footer">
        <div className="ip-footer__left">
          <span className="ip-footer__copy">© {YEAR} FantApp</span>
          <span className="ip-footer__sep" aria-hidden="true">·</span>
          <span className="ip-footer__ver">v{VERSION}</span>
        </div>
        <div className="ip-footer__right">
          <Heart size={12} className="ip-footer__heart" aria-hidden="true" />
          <span>Fatto con passione per il fantacalcio</span>
        </div>
      </footer>
    </main>
  );
}
